// The install dialog's *What it needs* pane (keepr-api KPR-1811, KPR-1812;
// plan v4 §5.12, platform-standards §8): reached only when a bundle's card
// that can use a card you have needs the person to choose. One Back link, the
// sections, then what changes on their cards and what is left out, in the
// server's words. The dialog owns the button; this pane only asks.
import React from 'react';
import { Alert, Button } from 'antd';

import InstallNeedSection from './InstallNeedSection';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import { CHEVRON_LEFT } from '../icons';
import { refusalText } from './InstallPreviewNotes';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { rowProblems } from '../../utils/packageBindings';

// What the pane says itself, or the count beside the button says: never an alert.
const SAID_HERE = new Set(['binding_needs_approval', 'requirement_unbound', 'binding_invalid']);
// A person has nothing to do about these (keepr-api KPR-1811: never in `warnings`).
const UNSAID = new Set(['stamp', 'groupRecord']);

export default function InstallNeedsPane({ packageName, collectionName, requirements, choices, onChoice, forecast, onRetry, onBack, leftLine = '' }) {
	const defs = useOptionalCardDefinitions();
	const elementsOf = cardId => (defs && typeof defs.getElementsForCard === 'function' ? defs.getElementsForCard(cardId) || [] : []);
	const preview = forecast.status === 'ready' ? forecast.data : null;
	const changes = Array.isArray(preview?.changes) ? preview.changes : [];
	const leftOut = (Array.isArray(preview?.leftOut) ? preview.leftOut : []).filter(x => !UNSAID.has(x.kind));
	const refusal = preview?.refusal || null;
	// A `binding_invalid` with problems is said on its rows; one without (a malformed body) is not.
	const saidHere = refusal && SAID_HERE.has(refusal.code) && (refusal.code !== 'binding_invalid' || (Array.isArray(refusal.problems) && refusal.problems.length > 0));
	const alert = refusal && !saidHere ? refusalText({ response: { data: refusal } }) : '';

	return (
		<div className="install-needs" data-testid="install-needs">
			<Button type="link" className="install-needs__back" icon={<CHEVRON_LEFT aria-hidden="true" />} onClick={onBack}>Back</Button>
			<h3 className="install-needs__title">What it needs</h3>
			<p className="meta-line install-needs__line">{`Choose what ${packageName} uses in ${collectionName}. Nothing changes on your cards until you add it.`}</p>

			{requirements.map(r => (
				<InstallNeedSection
					key={r.local}
					requirement={r}
					choice={choices[r.local] || null}
					onChange={next => onChoice(r.local, next)}
					problems={rowProblems(preview, r.local, { counted: !leftLine })}
					elementsOf={elementsOf}
				/>
			))}

			<section className="install-preview install-needs__changes" aria-label="What changes on your cards" aria-live="polite" aria-busy={forecast.status === 'loading'}>
				{forecast.status !== 'ready' ? (
					<DialogLoadState
						loaded={false}
						loading={forecast.status === 'loading'}
						error={forecast.status === 'failed'}
						label="what it would change on your cards"
						onRetry={onRetry}
						skeleton={<Skeleton variant="line" count={2} />}
						minHeight={48}
					/>
				) : (
					<>
						<h4 className="micro-label install-needs__sub">What changes on your cards</h4>
						{changes.length ? (
							<ul className="install-preview__list">
								{changes.map(c => <li key={`${c.kind}:${c.cardId}:${c.element || c.list || ''}:${c.value || ''}`}>{c.sentence}</li>)}
							</ul>
						) : <p className="meta-line install-preview__line">Nothing on your cards changes.</p>}
						{leftOut.length ? (
							<>
								<h4 className="micro-label install-needs__sub">Left out</h4>
								<ul className="install-preview__list">
									{leftOut.map(x => <li key={`${x.kind}:${x.card}:${x.name}`}>{x.message}</li>)}
								</ul>
							</>
						) : null}
					</>
				)}
			</section>
			{alert ? <Alert type="warning" showIcon className="install-preview__refusal" message={alert} /> : null}
		</div>
	);
}
